import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FolderInput, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const MoveToPeriodModal = ({ selectedClients, periods, onClose, onSuccess }) => {
  const [targetPeriod, setTargetPeriod] = useState('');
  const [loading, setLoading] = useState(false);

  const handleMove = async () => {
    if (!targetPeriod) {
      toast.error('Selecione um período de destino');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(
        `${API}/clients/batch/move-period`,
        {
          client_ids: selectedClients,
          period_id: targetPeriod
        },
        getAuthHeaders()
      );

      toast.success(`${response.data.updated_count} cliente(s) movido(s) com sucesso!`);
      onSuccess();
    } catch (error) {
      console.error('Move error:', error);
      toast.error(error.response?.data?.detail || 'Erro ao mover clientes');
    } finally {
      setLoading(false);
    }
  };

  const targetPeriodName = periods.find(p => p.id === targetPeriod)?.name;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]" data-testid="move-period-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="move-period-modal-title">
            <FolderInput className="w-5 h-5 text-primary" />
            Mover para Outro Período
          </DialogTitle>
          <DialogDescription>
            Mova os clientes selecionados para um novo período
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info Alert */}
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              <strong>{selectedClients.length}</strong> cliente(s) selecionado(s) serão movidos para o período escolhido.
            </AlertDescription>
          </Alert>

          {/* Period Selection */}
          <div className="space-y-2">
            <Label htmlFor="target-period">Período de Destino</Label>
            <Select value={targetPeriod} onValueChange={setTargetPeriod}>
              <SelectTrigger id="target-period" data-testid="target-period-select">
                <SelectValue placeholder="Selecione o período de destino" />
              </SelectTrigger>
              <SelectContent>
                {periods.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.status === 'ativo' ? '(Ativo)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          {targetPeriod && (
            <div className="bg-muted/30 p-3 rounded-lg">
              <p className="text-sm">
                <span className="text-muted-foreground">Os </span>
                <span className="font-medium">{selectedClients.length} cliente(s)</span>
                <span className="text-muted-foreground"> serão movidos para </span>
                <span className="font-medium text-primary">{targetPeriodName}</span>
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            data-testid="move-period-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleMove}
            disabled={loading || !targetPeriod}
            data-testid="move-period-confirm"
          >
            <FolderInput className="w-4 h-4 mr-2" />
            {loading ? 'Movendo...' : 'Mover Clientes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveToPeriodModal;
